import { ReactNode, useState } from 'react';
import BaseGraph from '../model/baseGraph';
import DynamicPositionedGraph from '../model/dynamicGraph';
import PositionedGraph from '../model/positionedGraph';

import { PedigreeContext, PedigreeContextType } from './PedigreeContext';
import { PedigreeCondition } from '../types';
import { useUndoRedo } from '../hooks/useUndoRedo';

// -----------------------------------------------------------------------------
interface PedigreeProviderProps {
  children: ReactNode;
}

// -----------------------------------------------------------------------------
export const PedigreeProvider: React.FC<PedigreeProviderProps> = ({
  children,
}) => {
  const [baseGraph, setBaseGraph] = useState<any>(null);
  const [positionedGraph, setPositionedGraph] = useState<any>(null);
  const [dynamicGraph, setDynamicGraph] =
    useState<DynamicPositionedGraph | null>(null);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [relationshipSelectionMode, setRelationshipSelectionMode] = useState<{
    isActive: boolean;
    firstNodeId: number | null;
  }>({
    isActive: false,
    firstNodeId: null,
  });

  const [extraData, setExtraData] = useState<{
    conditions: PedigreeCondition[];
  }>({
    conditions: [],
  });

  const [mode, setMode] = useState<'view' | 'edit'>('edit');
  const {
    canUndo,
    canRedo,
    undo: undoAction,
    redo: redoAction,
    pushState,
  } = useUndoRedo();

  // ---------------------------------------------------------------------------
  const saveStateForUndo = () => {
    if (baseGraph && extraData) {
      pushState(baseGraph, extraData);
    }
  };

  // ---------------------------------------------------------------------------
  const undo = () => {
    if (baseGraph && extraData) {
      const previousState = undoAction(baseGraph, extraData);
      if (previousState) {
        setBaseGraph(previousState.baseGraph);
        setExtraData(previousState.extraData);
        setRefreshFlag(true);
      }
    }
  };

  // ---------------------------------------------------------------------------
  const redo = () => {
    if (baseGraph && extraData) {
      const nextState = redoAction(baseGraph, extraData);
      if (nextState) {
        setBaseGraph(nextState.baseGraph);
        setExtraData(nextState.extraData);
        setRefreshFlag(true);
      }
    }
  };

  // ---------------------------------------------------------------------------
  const initializeGraphs = async () => {
    try {
      // Create new instances
      const newBaseGraph = new BaseGraph();

      const probandId = newBaseGraph.insertVertex(
        BaseGraph.TYPE.PERSON,
        {
          gender: 'M',
          isProband: true,
        },
        1,
        [],
        []
      );

      const pg = new PositionedGraph(newBaseGraph, 300, 300);

      const newDynamicGraph = new DynamicPositionedGraph(pg);

      newDynamicGraph.addNewParents(probandId);
      const parents = newDynamicGraph.DG.GG.getParents(probandId);
      if (parents) {
        newDynamicGraph.addNewParents(parents[0]);
        newDynamicGraph.addNewParents(parents[1]);
      }

      setBaseGraph(newDynamicGraph.DG.GG);
      setPositionedGraph(pg);
      setDynamicGraph(newDynamicGraph);
    } catch (error) {
      console.error('Failed to initialize graphs:', error);
    }
  };

  // ---------------------------------------------------------------------------
  const value: PedigreeContextType = {
    baseGraph,
    setBaseGraph,
    positionedGraph,
    setPositionedGraph,
    dynamicGraph,
    setDynamicGraph,
    initializeGraphs,
    refreshFlag,
    setRefreshFlag,
    selectedNodeId,
    setSelectedNodeId,
    extraData,
    setExtraData,
    mode,
    setMode,
    relationshipSelectionMode,
    setRelationshipSelectionMode,
    canUndo,
    canRedo,
    undo,
    redo,
    saveStateForUndo,
  };

  // ---------------------------------------------------------------------------
  return (
    <PedigreeContext.Provider value={value}>
      {children}
    </PedigreeContext.Provider>
  );
};
